// The painted collection: 100 invitation templates whose artwork is a set of
// images in public/templates/painted/<id>/ — a watercolour painting with
// painted cut-outs, a botanical plate on plain paper, a still-life photograph,
// or one-ink line drawings. The host's own title, date and venue are set on
// top by components/invite/PaintedCard. Ids must match INVITE_TEMPLATES in
// backend/server.py (backend/tests/test_invite_templates.py reads this file).
//
// `accents` and `papers` are the colours the Design step offers for a
// template, first one = the design's own; each was sampled from the artwork
// and checked for contrast against the surface the type sits on.

import type { PaintedDef } from './paintedInvite'

export const PAINTED_DEFS: PaintedDef[] = [
  {
    id: 'wc-under-the-sea',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Under the Sea', ka: 'ზღვის სიღრმე' },
    blurb: { en: 'Watercolour sea friends and a treasure chest', ka: 'აკვარელის ზღვის ბინადრები და განძის ზარდახშა' },
    tags: ['sea', 'ocean', 'whale', 'octopus', 'ზღვა', 'ოკეანე', 'ვეშაპი'],
    font: 'fredoka',
    paper: '#d3f2f1',
    zonePaper: '#bbeaea',
    ink: '#0D3B53',
    accents: ['#de4e38', '#c2456e', '#1f6f99', '#8a6420', '#0e5c6b', '#0d3b53'],
    typeset: 'rounded',
    date: 'block',
    zone: [21, 67],
    cx: 12,
    pieces: [
      { key: 'whale', pos: { right: 2, top: 9 }, w: 22, r: 1.038, motion: 'drift' },
      { key: 'octopus', pos: { left: -1, bottom: 2 }, w: 35, r: 1.187, motion: 'bob' },
      { key: 'chest', pos: { right: 3, bottom: 2 }, w: 29, r: 1.145, motion: 'breathe' },
    ],
  },
  {
    id: 'wc-dino-jungle',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Dino Jungle', ka: 'დინოზავრების ჯუნგლები' },
    blurb: { en: 'A party-hat T-rex, a hatching triceratops and jungle leaves', ka: 'ქუდიანი ტირანოზავრი, კვერცხიდან გამოსული ტრიცერატოპსი და ჯუნგლები' },
    tags: ['dinosaur', 'dino', 'jungle', 'დინოზავრი', 'ჯუნგლები'],
    font: 'luckiest-guy',
    paper: '#fdf8eb',
    zonePaper: '#fcf4e1',
    ink: '#2B3A26',
    accents: ['#e0652a', '#b86b12', '#4f7a24', '#1b5431', '#2f746f', '#32312d'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 64],
    cx: 12,
    pieces: [
      { key: 'pterodactyl', pos: { left: 3, top: 8 }, w: 26, r: 1.063, motion: 'drift' },
      { key: 'trex', pos: { left: 2, bottom: 3 }, w: 30, r: 1.562, motion: 'hop' },
      { key: 'triceratops', pos: { right: 4, bottom: 3 }, w: 24, r: 1.739, motion: 'bob' },
    ],
  },
  {
    id: 'wc-unicorn-dreams',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Unicorn Dreams', ka: 'ერთრქის სიზმრები' },
    blurb: { en: 'A rainbow unicorn, a unicorn cake and a pastel balloon', ka: 'ცისარტყელა ერთრქა, ტორტი და პასტელური ბუშტი' },
    tags: ['unicorn', 'rainbow', 'magic', 'ერთრქა', 'ცისარტყელა'],
    font: 'pacifico',
    paper: '#fef0f1',
    zonePaper: '#fde8e9',
    ink: '#5A3E72',
    accents: ['#c4468a', '#8660b5', '#2f7f7b', '#a07522', '#5a3e72'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 65],
    cx: 12,
    pieces: [
      { key: 'balloon', pos: { right: 6, top: 12 }, w: 17, r: 1.241, motion: 'drift' },
      { key: 'unicorn', pos: { left: 1, bottom: 4 }, w: 34, r: 1.275, motion: 'breathe' },
      { key: 'cake', pos: { right: 5, bottom: 4 }, w: 24, r: 1.683, motion: 'hop' },
    ],
  },
  {
    id: 'wc-space-rocket',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Rocket Adventure', ka: 'კოსმოსური თავგადასავალი' },
    blurb: { en: 'Watercolour rocket, planets and a teddy astronaut', ka: 'აკვარელის რაკეტა, პლანეტები და დათუნია-ასტრონავტი' },
    tags: ['space', 'rocket', 'planet', 'astronaut', 'კოსმოსი', 'რაკეტა', 'პლანეტა'],
    font: 'fredoka',
    paper: '#f1edfc',
    zonePaper: '#e9e4fa',
    ink: '#2E3566',
    accents: ['#e0552f', '#a27535', '#5b84bb', '#9f6d65', '#9370a8', '#2e3566'],
    typeset: 'rounded',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'rocket', pos: { right: 4, top: 9 }, w: 14.4, r: 1.557, motion: 'drift' },
      { key: 'astronaut', pos: { left: 1, bottom: 3 }, w: 29.2, r: 1.49, motion: 'hop' },
      { key: 'planet', pos: { right: 3, bottom: 3 }, w: 29, r: 0.881, motion: 'spin' },
    ],
  },
  {
    id: 'wc-race-day',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Race Day', ka: 'რბოლის დღე' },
    blurb: { en: 'Race cars, checkered flags and a golden cup', ka: 'სარბოლო მანქანები, ჭადრაკული დროშები და ოქროს თასი' },
    tags: ['race', 'car', 'cars', 'racing', 'მანქანა', 'რბოლა'],
    font: 'luckiest-guy',
    paper: '#fefaf0',
    zonePaper: '#fdf7e8',
    ink: '#2B2B2B',
    accents: ['#d2322a', '#6f8c9c', '#c57807', '#a38627', '#924e19', '#2b2b2b'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'car', pos: { left: 1, bottom: 3 }, w: 32, r: 0.429, motion: 'drift' },
      { key: 'trophy', pos: { right: 3, bottom: 3 }, w: 29, r: 1.187, motion: 'breathe' },
      { key: 'flag', pos: { right: 4, top: 9 }, w: 21, r: 0.735, motion: 'sway' },
    ],
  },
  {
    id: 'wc-royal-princess',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Royal Princess', ka: 'სამეფო პრინცესა' },
    blurb: { en: 'A pink castle, a jewelled crown and a pumpkin carriage', ka: 'ვარდისფერი კოშკი, ძვირფასი გვირგვინი და გოგრის ეტლი' },
    tags: ['princess', 'castle', 'crown', 'fairytale', 'პრინცესა', 'ზღაპარი', 'გვირგვინი'],
    font: 'pacifico',
    paper: '#feefed',
    zonePaper: '#fde6e3',
    ink: '#6B3A5B',
    accents: ['#c23e7e', '#b06f79', '#9d7b4d', '#cb6556', '#6b3a5b'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'castle', pos: { right: 3, bottom: 3 }, w: 28.9, r: 1.402, motion: 'breathe' },
      { key: 'crown', pos: { right: 4, top: 9 }, w: 21, r: 0.85, motion: 'hop' },
      { key: 'carriage', pos: { left: 1, bottom: 3 }, w: 32, r: 0.994, motion: 'bob' },
    ],
  },
  {
    id: 'wc-pirate-cove',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Pirate Cove', ka: 'მეკობრეების ყურე' },
    blurb: { en: 'A pirate ship, a parrot and a treasure map', ka: 'მეკობრეთა ხომალდი, თუთიყუში და განძის რუკა' },
    tags: ['pirate', 'ship', 'treasure', 'parrot', 'მეკობრე', 'ხომალდი', 'განძი'],
    font: 'luckiest-guy',
    paper: '#fdf5e3',
    zonePaper: '#fdefd4',
    ink: '#203A4C',
    accents: ['#b5382b', '#ac5e27', '#174d7a', '#808e33', '#9d7b4b', '#203a4c'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'ship', pos: { right: 3, bottom: 3 }, w: 29, r: 1.133, motion: 'drift' },
      { key: 'parrot', pos: { left: 4, top: 9 }, w: 12.9, r: 1.745, motion: 'hop' },
      { key: 'map', pos: { left: 1, bottom: 3 }, w: 32, r: 0.775, motion: 'sway' },
    ],
  },
  {
    id: 'wc-safari-friends',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Safari Friends', ka: 'საფარის მეგობრები' },
    blurb: { en: 'A baby giraffe, an elephant and a swinging monkey', ka: 'ჟირაფი, სპილო და ტოტებზე მოქანავე მაიმუნი' },
    tags: ['safari', 'jungle', 'giraffe', 'elephant', 'animals', 'საფარი', 'ცხოველები', 'ჟირაფი'],
    font: 'luckiest-guy',
    paper: '#fef6dd',
    zonePaper: '#fdf1ca',
    ink: '#4A3B22',
    accents: ['#c9651c', '#a18766', '#6991a1', '#808a28', '#4a3b22'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'giraffe', pos: { left: 1, bottom: 3 }, w: 20.1, r: 2.158, motion: 'sway' },
      { key: 'elephant', pos: { right: 3, bottom: 3 }, w: 29, r: 1.163, motion: 'bob' },
      { key: 'monkey', pos: { right: 4, top: 9 }, w: 15.2, r: 1.481, motion: 'sway' },
    ],
  },
  {
    id: 'wc-farm-friends',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Farm Friends', ka: 'ფერმის მეგობრები' },
    blurb: { en: 'A spotted cow, a red tractor and a yellow chick', ka: 'ჭრელი ძროხა, წითელი ტრაქტორი და ყვითელი წიწილა' },
    tags: ['farm', 'cow', 'tractor', 'animals', 'ფერმა', 'ძროხა', 'ტრაქტორი'],
    font: 'fredoka',
    paper: '#fcf9ed',
    zonePaper: '#fbf6e4',
    ink: '#3A4A2A',
    accents: ['#c8452f', '#b0884d', '#b97866', '#db6a1d', '#3a4a2a'],
    typeset: 'rounded',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'cow', pos: { left: 1, bottom: 3 }, w: 32, r: 1.241, motion: 'bob' },
      { key: 'tractor', pos: { right: 3, bottom: 3 }, w: 29, r: 0.84, motion: 'drift' },
      { key: 'chick', pos: { right: 4, top: 9 }, w: 14.5, r: 1.552, motion: 'hop' },
    ],
  },
  {
    id: 'wc-construction-zone',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Construction Zone', ka: 'სამშენებლო ზონა' },
    blurb: { en: 'A yellow digger, a dump truck and traffic cones', ka: 'ყვითელი ექსკავატორი, თვითმცლელი და კონუსები' },
    tags: ['construction', 'digger', 'excavator', 'truck', 'მშენებლობა', 'ექსკავატორი', 'მანქანა'],
    font: 'luckiest-guy',
    paper: '#fef7e9',
    zonePaper: '#fdf3dc',
    ink: '#333333',
    accents: ['#cd6b00', '#a48624', '#4188bd', '#f45a09', '#333333'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'digger', pos: { left: 1, bottom: 3 }, w: 32, r: 0.931, motion: 'bob' },
      { key: 'truck', pos: { right: 3, bottom: 3 }, w: 29, r: 0.669, motion: 'drift' },
      { key: 'cone', pos: { right: 4, top: 9 }, w: 14.8, r: 1.52, motion: 'hop' },
    ],
  },
  {
    id: 'wc-little-hero',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Little Hero', ka: 'პატარა გმირი' },
    blurb: { en: 'A caped teddy hero, a star shield and a robot sidekick', ka: 'ლაბადიანი დათუნია-გმირი, ვარსკვლავიანი ფარი და რობოტი' },
    tags: ['superhero', 'hero', 'cape', 'robot', 'გმირი', 'სუპერგმირი'],
    font: 'luckiest-guy',
    paper: '#fefae4',
    zonePaper: '#fdf7d6',
    ink: '#1F3A6B',
    accents: ['#d62e2e', '#af7c30', '#c3621a', '#318ce2', '#1394aa', '#1f3a6b'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'teddy', pos: { right: 4, top: 9 }, w: 21, r: 0.879, motion: 'drift' },
      { key: 'shield', pos: { right: 3, bottom: 3 }, w: 29, r: 1.026, motion: 'breathe' },
      { key: 'robot', pos: { left: 1, bottom: 3 }, w: 28.9, r: 1.507, motion: 'hop' },
    ],
  },
  {
    id: 'wc-fairy-garden',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Fairy Garden', ka: 'ფერიების ბაღი' },
    blurb: { en: 'A mushroom house, a lantern and butterflies', ka: 'სოკოს სახლი, ფარანი და პეპლები' },
    tags: ['fairy', 'garden', 'mushroom', 'magic', 'ფერია', 'ბაღი', 'ჯადოსნური'],
    font: 'pacifico',
    paper: '#f4f8ee',
    zonePaper: '#eef5e5',
    ink: '#3F4A3A',
    accents: ['#8a4fb3', '#f1442e', '#bf7552', '#918141', '#b2738a', '#3f4a3a'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'house', pos: { left: 1, bottom: 3 }, w: 32, r: 1.275, motion: 'breathe' },
      { key: 'lantern', pos: { right: 3, bottom: 3 }, w: 29, r: 1.354, motion: 'sway' },
      { key: 'butterflies', pos: { right: 4, top: 9 }, w: 19.2, r: 1.169, motion: 'drift' },
    ],
  },
  {
    id: 'wc-big-top-circus',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Big Top Circus', ka: 'დიდი ცირკი' },
    blurb: { en: 'A striped big top, an elephant on a ball and a juggling seal', ka: 'ზოლიანი ცირკი, ბურთზე სპილო და ჟონგლიორი სელაპი' },
    tags: ['circus', 'carnival', 'tent', 'ცირკი', 'კარნავალი'],
    font: 'luckiest-guy',
    paper: '#fdf6e7',
    zonePaper: '#fcf1da',
    ink: '#26314F',
    accents: ['#c8302f', '#ad7d30', '#286298', '#ac7860', '#26314f'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'tent', pos: { right: 3, bottom: 3 }, w: 29, r: 0.95, motion: 'breathe' },
      { key: 'elephant', pos: { left: 1, bottom: 3 }, w: 27, r: 1.61, motion: 'bob' },
      { key: 'seal', pos: { right: 4, top: 9 }, w: 15.3, r: 1.469, motion: 'hop' },
    ],
  },
  {
    id: 'wc-butterfly-meadow',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Butterfly Meadow', ka: 'პეპლების მდელო' },
    blurb: { en: 'Butterflies, a ladybird and a watering can of flowers', ka: 'პეპლები, ჭიამაია და ყვავილებით სავსე სარწყავი' },
    tags: ['butterfly', 'flowers', 'spring', 'garden', 'პეპელა', 'ყვავილები', 'გაზაფხული'],
    font: 'pacifico',
    paper: '#fef9e6',
    zonePaper: '#fdf6d9',
    ink: '#3E5A2E',
    accents: ['#d2427f', '#a38821', '#5694bd', '#285e9e', '#d46252', '#3e5a2e'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'butterflies', pos: { right: 4, top: 9 }, w: 21, r: 1.022, motion: 'drift' },
      { key: 'can', pos: { left: 1, bottom: 3 }, w: 32, r: 0.954, motion: 'sway' },
      { key: 'ladybird', pos: { right: 3, bottom: 3 }, w: 29, r: 0.84, motion: 'hop' },
    ],
  },
  {
    id: 'wc-dragon-castle',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Dragon & Knight', ka: 'დრაკონი და რაინდი' },
    blurb: { en: 'A friendly dragon, a toy sword and a golden egg', ka: 'კეთილი დრაკონი, სათამაშო ხმალი და ოქროს კვერცხი' },
    tags: ['dragon', 'knight', 'castle', 'medieval', 'დრაკონი', 'რაინდი', 'ციხე'],
    font: 'luckiest-guy',
    paper: '#f3f8ea',
    zonePaper: '#ecf4df',
    ink: '#2F3B45',
    accents: ['#2e7d5b', '#94874d', '#214827', '#ae781e', '#2f3b45'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'dragon', pos: { left: 1, bottom: 3 }, w: 32, r: 1.215, motion: 'bob' },
      { key: 'sword', pos: { right: 3, bottom: 3 }, w: 29, r: 1.323, motion: 'sway' },
      { key: 'egg', pos: { right: 4, top: 9 }, w: 21, r: 0.879, motion: 'breathe' },
    ],
  },
  {
    id: 'wc-rock-star',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Little Rock Star', ka: 'პატარა როკ-ვარსკვლავი' },
    blurb: { en: 'An electric guitar, a microphone and flying music notes', ka: 'ელექტროგიტარა, მიკროფონი და ნოტები' },
    tags: ['music', 'rock', 'guitar', 'pop star', 'მუსიკა', 'გიტარა', 'ვარსკვლავი'],
    font: 'bebas',
    paper: '#feeeed',
    zonePaper: '#fde5e4',
    ink: '#3A2A55',
    accents: ['#d93f7c', '#ad6f4b', '#a27f2a', '#a77385', '#c265b3', '#3a2a55'],
    typeset: 'poster-bold',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'guitar', pos: { left: 1, bottom: 3 }, w: 31.9, r: 1.365, motion: 'sway' },
      { key: 'mic', pos: { right: 3, bottom: 3 }, w: 14, r: 2.889, motion: 'bob' },
      { key: 'notes', pos: { right: 4, top: 9 }, w: 19.7, r: 1.145, motion: 'drift' },
    ],
  },
  {
    id: 'wc-art-party',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Art Party', ka: 'ხელოვნების წვეულება' },
    blurb: { en: 'A paint palette, an easel and jars of brushes', ka: 'საღებავების პალიტრა, მოლბერტი და ფუნჯები' },
    tags: ['art', 'paint', 'craft', 'painting', 'ხელოვნება', 'ხატვა'],
    font: 'fredoka',
    paper: '#fdfdfc',
    zonePaper: '#fdfcfb',
    ink: '#333333',
    accents: ['#2f6fd0', '#b2844a', '#f16b11', '#e21809', '#a38202', '#333333'],
    typeset: 'rounded',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'palette', pos: { left: 1, bottom: 3 }, w: 32, r: 0.844, motion: 'sway' },
      { key: 'easel', pos: { right: 3, bottom: 3 }, w: 20.7, r: 1.955, motion: 'breathe' },
      { key: 'brushes', pos: { right: 4, top: 9 }, w: 11.5, r: 1.948, motion: 'hop' },
    ],
  },
  {
    id: 'wc-soccer-star',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Soccer Star', ka: 'ფეხბურთის ვარსკვლავი' },
    blurb: { en: 'A football, a goal net and a golden trophy', ka: 'ბურთი, კარი და ოქროს თასი' },
    tags: ['soccer', 'football', 'sport', 'ფეხბურთი', 'სპორტი', 'ბურთი'],
    font: 'luckiest-guy',
    paper: '#f4f9eb',
    zonePaper: '#eef6e1',
    ink: '#1E2B3A',
    accents: ['#1f7a3a', '#668faa', '#b67504', '#1e2b3a'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'ball', pos: { left: 1, bottom: 3 }, w: 32, r: 0.7, motion: 'spin' },
      { key: 'goal', pos: { right: 3, bottom: 3 }, w: 29, r: 0.642, motion: 'breathe' },
      { key: 'trophy', pos: { right: 4, top: 9 }, w: 17, r: 1.327, motion: 'hop' },
    ],
  },
  {
    id: 'wc-little-baker',
    look: 'watercolor',
    category: 'kids',
    label: { en: 'Little Baker', ka: 'პატარა კონდიტერი' },
    blurb: { en: 'A teddy chef, a tray of cupcakes and a rolling pin', ka: 'დათუნია-მზარეული, ნამცხვრები და საგორავი' },
    tags: ['baking', 'cooking', 'chef', 'cupcake', 'ცხობა', 'მზარეული', 'ნამცხვარი'],
    font: 'pacifico',
    paper: '#fef7eb',
    zonePaper: '#fdf3e0',
    ink: '#5A3A2E',
    accents: ['#cf5a6c', '#b08554', '#c5722e', '#b27169', '#f6427e', '#5a3a2e'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'teddy', pos: { left: 1, bottom: 3 }, w: 24.5, r: 1.775, motion: 'hop' },
      { key: 'cupcakes', pos: { right: 3, bottom: 3 }, w: 29, r: 0.773, motion: 'breathe' },
      { key: 'pin', pos: { right: 4, top: 9 }, w: 19, r: 1.182, motion: 'sway' },
    ],
  },
  {
    id: 'line-little-ballerina',
    look: 'line',
    category: 'kids',
    label: { en: 'Little Ballerina', ka: 'პატარა ბალერინა' },
    blurb: { en: 'Pointe shoes and a music box in rose ink', ka: 'პუანტები და მუსიკალური ყუთი' },
    tags: ['ballet', 'ballerina', 'dance', 'ბალეტი', 'ბალერინა'],
    font: 'pinyon-montserrat',
    paper: '#F7E8EA',
    ink: '#AE3A5A',
    accents: ['#AE3A5A', '#a8141b', '#1f3a68', '#2e5a3e', '#6b2d5c', '#7a4a26'],
    papers: ['#F7E8EA', '#f6f1e7', '#e9efe6', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.283 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 32.9, r: 1.822 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 32.9, r: 1.822 },
    },
    zone: [16, 70],
  },
  {
    id: 'line-choo-choo',
    look: 'line',
    category: 'kids',
    label: { en: 'Choo-Choo Train', ka: 'ჩუ-ჩუ მატარებელი' },
    blurb: { en: 'A steam train and toy wagons in blue ink', ka: 'ორთქლმავალი და სათამაშო ვაგონები' },
    tags: ['train', 'railway', 'toy', 'მატარებელი'],
    font: 'pinyon-montserrat',
    paper: '#EAF1F6',
    ink: '#1F4E79',
    accents: ['#1F4E79', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#EAF1F6', '#f6f1e7', '#f4e6dd', '#e9efe6', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.14 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.596 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 0.794 },
    },
    zone: [14, 70],
  },
  {
    id: 'line-teddy-picnic',
    look: 'line',
    category: 'kids',
    label: { en: 'Teddy Bear Picnic', ka: 'დათუნიების პიკნიკი' },
    blurb: { en: 'Teddy bears, a basket and a teapot', ka: 'დათუნიები, კალათა და ჩაიდანი' },
    tags: ['teddy', 'picnic', 'bear', 'დათუნია', 'პიკნიკი'],
    font: 'pinyon-montserrat',
    paper: '#F6EFE3',
    ink: '#7A4A26',
    accents: ['#7A4A26', '#a8141b', '#1f3a68', '#2e5a3e', '#6b2d5c', '#1c1c1c'],
    papers: ['#F6EFE3', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.239 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.406 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.058 },
    },
    zone: [14, 70],
  },
  {
    id: 'wc-golden-birthday',
    look: 'watercolor',
    category: 'birthday',
    label: { en: 'Golden Birthday', ka: 'ოქროსფერი დაბადების დღე' },
    blurb: { en: 'Blush and gold balloons, champagne and a drip cake', ka: 'ვარდისფერ-ოქროსფერი ბუშტები, შამპანიური და ტორტი' },
    tags: ['gold', 'balloons', 'champagne', 'milestone', 'ოქრო', 'ბუშტები', 'იუბილე'],
    font: 'great-vibes',
    paper: '#fdf8f2',
    zonePaper: '#fbf4eb',
    ink: '#4A3530',
    accents: ['#a27428', '#b25f6e', '#9c5f44', '#4a3530', '#2f2b28'],
    typeset: 'glam-script',
    date: 'line',
    zone: [25, 64],
    cx: 17,
    pieces: [
      { key: 'cake', pos: { left: 4, bottom: 3 }, w: 27, r: 1.688, motion: 'breathe' },
      { key: 'champagne', pos: { right: 3, bottom: 3 }, w: 29, r: 1.585, motion: 'sway' },
    ],
  },
  {
    id: 'wc-candy-land',
    look: 'watercolor',
    category: 'birthday',
    label: { en: 'Candy Land', ka: 'კანფეტების ქვეყანა' },
    blurb: { en: 'Swirl lollipops, a candy jar and macarons', ka: 'ლეღვაკანფეტები, კანფეტის ქილა და მაკარონები' },
    tags: ['candy', 'sweets', 'lollipop', 'კანფეტი', 'ტკბილეული'],
    font: 'pacifico',
    paper: '#fef2f0',
    zonePaper: '#fdebe8',
    ink: '#5B3A5E',
    accents: ['#d83f78', '#a27b31', '#c46b29', '#c46483', '#6488a9', '#5b3a5e'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'lollipop', pos: { right: 4, top: 9 }, w: 11.1, r: 2.031, motion: 'sway' },
      { key: 'jar', pos: { right: 3, bottom: 3 }, w: 23.8, r: 1.699, motion: 'breathe' },
      { key: 'macarons', pos: { left: 1, bottom: 3 }, w: 32, r: 1.166, motion: 'hop' },
    ],
  },
  {
    id: 'wc-ice-cream-social',
    look: 'watercolor',
    category: 'birthday',
    label: { en: 'Ice Cream Social', ka: 'ნაყინის წვეულება' },
    blurb: { en: 'A triple scoop cone, a sundae and popsicles', ka: 'სამმაგი ნაყინი, სანდეი და ესკიმოები' },
    tags: ['ice cream', 'summer', 'dessert', 'ნაყინი', 'ზაფხული'],
    font: 'fredoka',
    paper: '#fcf9ec',
    zonePaper: '#faf6e1',
    ink: '#2F5555',
    accents: ['#dc5e84', '#b07a2d', '#f44658', '#d26714', '#5c9c73', '#2f5555'],
    typeset: 'rounded',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'cone', pos: { left: 1, bottom: 3 }, w: 21.2, r: 2.047, motion: 'hop' },
      { key: 'sundae', pos: { right: 3, bottom: 3 }, w: 21.1, r: 1.919, motion: 'breathe' },
      { key: 'popsicles', pos: { right: 4, top: 9 }, w: 17.8, r: 1.268, motion: 'sway' },
    ],
  },
  {
    id: 'wc-roller-disco',
    look: 'watercolor',
    category: 'birthday',
    label: { en: 'Roller Disco', ka: 'როლერ დისკო' },
    blurb: { en: 'A pink roller skate, a boombox and a disco ball', ka: 'ვარდისფერი ციგურა, მაგნიტოფონი და დისკო-ბურთი' },
    tags: ['disco', 'roller', 'retro', '80s', 'დისკო', 'რეტრო'],
    font: 'bebas',
    paper: '#fdedf3',
    zonePaper: '#fde4ec',
    ink: '#3B2F6B',
    accents: ['#d6337a', '#b36a86', '#927f40', '#9576aa', '#3b2f6b'],
    typeset: 'poster-bold',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'skate', pos: { left: 1, bottom: 3 }, w: 32, r: 1.224, motion: 'drift' },
      { key: 'boombox', pos: { right: 3, bottom: 3 }, w: 29, r: 0.688, motion: 'bob' },
      { key: 'disco', pos: { right: 4, top: 9 }, w: 15.6, r: 1.44, motion: 'spin' },
    ],
  },
  {
    id: 'wc-bows-pearls',
    look: 'watercolor',
    category: 'birthday',
    label: { en: 'Bows & Pearls', ka: 'ბაფთები და მარგალიტები' },
    blurb: { en: 'Satin bows, pearls and a heart cake', ka: 'ატლასის ბაფთები, მარგალიტები და გულისებრი ტორტი' },
    tags: ['bows', 'pearls', 'coquette', 'pink', 'ბაფთა', 'მარგალიტი'],
    font: 'great-vibes',
    paper: '#fdefee',
    zonePaper: '#fde6e5',
    ink: '#5A3A44',
    accents: ['#bd4e78', '#9e7170', '#5a3a44'],
    typeset: 'glam-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'cake', pos: { right: 3, bottom: 3 }, w: 29, r: 1.166, motion: 'breathe' },
      { key: 'glass', pos: { left: 1, bottom: 3 }, w: 22.1, r: 1.97, motion: 'sway' },
      { key: 'bow', pos: { right: 4, top: 9 }, w: 17.2, r: 1.31, motion: 'hop' },
    ],
  },
  {
    id: 'bot-garden-roses',
    look: 'botanical',
    category: 'birthday',
    label: { en: 'Garden Roses', ka: 'ბაღის ვარდები' },
    blurb: { en: 'Apricot garden roses on plain paper', ka: 'ატმისფერი ბაღის ვარდები' },
    tags: ['roses', 'flowers', 'ვარდი', 'ყვავილები'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#a85526', '#c63a1c', '#50612d', '#866037', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 1.888 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-sunflowers',
    look: 'botanical',
    category: 'birthday',
    label: { en: 'Sunflowers', ka: 'მზესუმზირები' },
    blurb: { en: 'Tall golden sunflowers', ka: 'მაღალი ოქროსფერი მზესუმზირები' },
    tags: ['sunflower', 'summer', 'მზესუმზირა', 'ზაფხული'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#8c6009', '#5d6e37', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 1.836 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'photo-birthday-slice',
    look: 'photo',
    category: 'birthday',
    label: { en: 'A Slice of Birthday', ka: 'ტორტის ნაჭერი' },
    blurb: { en: 'A slice of cake with one candle', ka: 'ტორტის ნაჭერი ერთი სანთლით' },
    tags: ['cake', 'birthday', 'ტორტი', 'დაბადების დღე'],
    font: 'archivo',
    paper: '#dad1ce',
    zonePaper: '#bcaca5',
    ink: '#2a2224',
    accents: ['#2a2224', '#7d0c17', '#4a2e3a', '#5a3e24'],
    subject: { left: 17.25, top: 28.58, w: 75.75, r: 1.01 },
  },
  {
    id: 'photo-pizza-night',
    look: 'photo',
    category: 'birthday',
    label: { en: 'Pizza Night', ka: 'პიცის ღამე' },
    blurb: { en: 'A sunlit margherita pizza, poster style', ka: 'მზის შუქზე მარგარიტა, პოსტერის სტილში' },
    tags: ['pizza', 'party', 'პიცა'],
    font: 'archivo',
    paper: '#eee9e2',
    zonePaper: '#e0d6ca',
    ink: '#242220',
    accents: ['#242220', '#b35219', '#936833'],
    subject: { left: 10.12, top: 30, w: 83.38, r: 0.829 },
  },
  {
    id: 'photo-balloons',
    look: 'photo',
    category: 'birthday',
    label: { en: 'Balloon Wall', ka: 'ბუშტების კედელი' },
    blurb: { en: 'Pastel balloons in hard sunlight', ka: 'პასტელური ბუშტები მზის შუქზე' },
    tags: ['balloons', 'birthday', 'ბუშტები'],
    font: 'archivo',
    paper: '#ece8e3',
    zonePaper: '#dcd5cb',
    ink: '#232221',
    accents: ['#232221', '#a36c47'],
    subject: { left: 22.88, top: 38.08, w: 57.5, r: 0.878 },
  },
  {
    id: 'photo-champagne-tower',
    look: 'photo',
    category: 'birthday',
    label: { en: 'Champagne Toast', ka: 'შამპანიურის სადღეგრძელო' },
    blurb: { en: 'A small champagne tower in sunlight', ka: 'შამპანიურის პატარა კოშკი მზის შუქზე' },
    tags: ['champagne', 'toast', 'celebration', 'შამპანიური'],
    font: 'archivo',
    paper: '#eadad3',
    zonePaper: '#d9bbaf',
    ink: '#231e1c',
    accents: ['#231e1c', '#705b39'],
    subject: { left: 19.5, top: 25.17, w: 59.88, r: 1.23 },
  },
  {
    id: 'line-bows-cake',
    look: 'line',
    category: 'birthday',
    label: { en: 'Ribbons & Cake', ka: 'ბაფთები და ტორტი' },
    blurb: { en: 'Ribbon swags, a champagne tower and a tall cake', ka: 'ბაფთები, შამპანიურის კოშკი და მაღალი ტორტი' },
    tags: ['bows', 'ribbon', 'cake', 'ბაფთა', 'ტორტი'],
    font: 'pinyon-montserrat',
    paper: '#F4E6DD',
    ink: '#A8141B',
    accents: ['#A8141B', '#1f3a68', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#F4E6DD', '#e9efe6', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.421 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.63 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 33.7, r: 1.782 },
    },
    zone: [20, 70],
  },
  {
    id: 'line-cocktail-hour',
    look: 'line',
    category: 'birthday',
    label: { en: 'Cocktail Hour', ka: 'კოქტეილის საათი' },
    blurb: { en: 'A martini, a shaker and a coupe', ka: 'მართინი, შეიკერი და ფიალა' },
    tags: ['cocktail', 'martini', 'drinks', 'კოქტეილი'],
    font: 'pinyon-montserrat',
    paper: '#EAF0F4',
    ink: '#1F3A68',
    accents: ['#1F3A68', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#EAF0F4', '#f6f1e7', '#f4e6dd', '#e9efe6', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.149 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.4 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.636 },
    },
    zone: [14, 70],
  },
  {
    id: 'line-flower-market',
    look: 'line',
    category: 'birthday',
    label: { en: 'Flower Market', ka: 'ყვავილების ბაზარი' },
    blurb: { en: 'A bicycle with a flower basket and peonies', ka: 'ველოსიპედი ყვავილების კალათით და პეონები' },
    tags: ['flowers', 'bicycle', 'market', 'ყვავილები'],
    font: 'pinyon-montserrat',
    paper: '#F1EEE4',
    ink: '#2E5A3E',
    accents: ['#2E5A3E', '#a8141b', '#1f3a68', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#F1EEE4', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.324 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.456 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.538 },
    },
    zone: [18, 70],
  },
  {
    id: 'wc-eucalyptus-vows',
    look: 'watercolor',
    category: 'wedding',
    label: { en: 'Eucalyptus & Roses', ka: 'ევკალიპტი და ვარდები' },
    blurb: { en: 'Loose eucalyptus, white roses and a pair of doves', ka: 'ევკალიპტი, თეთრი ვარდები და მტრედები' },
    tags: ['eucalyptus', 'greenery', 'roses', 'wedding', 'ევკალიპტი', 'ქორწილი'],
    font: 'pinyon-bricolage',
    paper: '#faf7f0',
    zonePaper: '#f8f3e9',
    ink: '#3D4237',
    accents: ['#4f6446', '#6b7f5c', '#b56b72', '#8c6d3f', '#3d4237'],
    typeset: 'garden-script',
    date: 'line',
    zone: [27, 74],
    cx: 15,
    pieces: [
      { key: 'doves', pos: { right: 3, top: 5 }, w: 36, r: 0.979, motion: 'drift' },
      { key: 'posy', pos: { left: 6, bottom: 5 }, w: 24, r: 1.138, motion: 'sway' },
    ],
  },
  {
    id: 'wc-seaside-vows',
    look: 'watercolor',
    category: 'wedding',
    label: { en: 'Seaside Vows', ka: 'ზღვისპირა ფიცი' },
    blurb: { en: 'A flower arch on the sand, shells and doves', ka: 'ყვავილების თაღი ქვიშაზე, ნიჟარები და მტრედები' },
    tags: ['beach', 'sea', 'wedding', 'summer', 'ზღვა', 'ქორწილი', 'სანაპირო'],
    font: 'pinyon-bricolage',
    paper: '#fcf5e7',
    zonePaper: '#faf0da',
    ink: '#3A4A4F',
    accents: ['#2b7580', '#ad7e2a', '#848b4a', '#b1785f', '#3a4a4f'],
    typeset: 'garden-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'arch', pos: { right: 3, bottom: 3 }, w: 29, r: 1.032, motion: 'breathe' },
      { key: 'shells', pos: { left: 1, bottom: 3 }, w: 32, r: 0.771, motion: 'bob' },
      { key: 'doves', pos: { right: 4, top: 9 }, w: 20.2, r: 1.113, motion: 'drift' },
    ],
  },
  {
    id: 'bot-magnolia',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Magnolia', ka: 'მაგნოლია' },
    blurb: { en: 'A vintage magnolia plate and handwritten names', ka: 'ძველი მაგნოლიის ილუსტრაცია და ხელნაწერი სახელები' },
    tags: ['magnolia', 'wedding', 'botanical', 'მაგნოლია', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#3e5714', '#a8605a', '#7d5f28', '#5f6350'],
    papers: ['#f3f0e8', '#f0e5e0', '#e7ebe1', '#e9e3d6', '#223127', '#1e2331'],
    plant: { left: -7, bottom: -2, width: 44, r: 2.09 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-peony',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Blush Peony', ka: 'ვარდისფერი პიონი' },
    blurb: { en: 'Blush peonies for an engagement', ka: 'ვარდისფერი პიონები ნიშნობისთვის' },
    tags: ['peony', 'engagement', 'wedding', 'პიონი', 'ნიშნობა'],
    font: 'delafield-cormorant',
    paper: '#f6ede9',
    ink: '#3b3033',
    accents: ['#3b3033', '#b35f69', '#4d6a3a', '#8e4f5d', '#6e6048'],
    papers: ['#f6ede9', '#f3f0e8', '#eef0e6', '#f3e2df', '#2b2427', '#233026'],
    plant: { left: -9, bottom: -2, width: 50, r: 1.879 },
    copy: { left: 42, right: 5, top: 19 },
  },
  {
    id: 'bot-white-iris',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'White Iris', ka: 'თეთრი ზამბახი' },
    blurb: { en: 'White irises with a touch of yellow', ka: 'თეთრი ზამბახები ყვითელი ელფერით' },
    tags: ['iris', 'wedding', 'ზამბახი', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#836b15', '#57682c', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 2.535 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-anemones',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Anemones', ka: 'ანემონები' },
    blurb: { en: 'White anemones with dark centres', ka: 'თეთრი ანემონები მუქი გულით' },
    tags: ['anemone', 'wedding', 'ანემონე', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#616a2f', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 1.81 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-lily-valley',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Lily of the Valley', ka: 'მაისის შროშანა' },
    blurb: { en: 'Delicate bells of lily of the valley', ka: 'მაისის შროშანას ნაზი ყვავილები' },
    tags: ['lily of the valley', 'wedding', 'შროშანა', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#3b5828', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 2.041 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-wild-rose',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Wild Rose', ka: 'ასკილი' },
    blurb: { en: 'A climbing branch of pale wild roses', ka: 'ღია ვარდისფერი ასკილის ტოტი' },
    tags: ['wild rose', 'wedding', 'ასკილი', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#845b61', '#596a34', '#896830', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 2.393 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-lavender',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Lavender', ka: 'ლავანდა' },
    blurb: { en: 'A tall bunch of lavender', ka: 'ლავანდის მაღალი თაიგული' },
    tags: ['lavender', 'wedding', 'provence', 'ლავანდა', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#4e356f', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 2.015 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'bot-grapevine',
    look: 'botanical',
    category: 'wedding',
    label: { en: 'Vineyard Vows', ka: 'ვაზის ლერწი' },
    blurb: { en: 'A grapevine with ripe Saperavi grapes', ka: 'ვაზის ლერწი მწიფე საფერავით' },
    tags: ['vineyard', 'grapes', 'wine', 'wedding', 'ვაზი', 'ყურძენი', 'ქორწილი'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#87661b', '#556827', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -9.5, bottom: -2, width: 47.5, r: 2.718 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'photo-wedding-rings',
    look: 'photo',
    category: 'wedding',
    label: { en: 'Golden Rings', ka: 'ოქროს ბეჭდები' },
    blurb: { en: 'Two gold rings on linen', ka: 'ორი ოქროს ბეჭედი სელის ქსოვილზე' },
    tags: ['rings', 'wedding', 'engagement', 'ბეჭდები', 'ქორწილი'],
    font: 'archivo',
    paper: '#e6e0d9',
    zonePaper: '#d2c8ba',
    ink: '#22201e',
    accents: ['#22201e', '#5c3608'],
    subject: { left: 12.88, top: 35.58, w: 59.5, r: 0.702 },
  },
  {
    id: 'photo-bridal-bouquet',
    look: 'photo',
    category: 'wedding',
    label: { en: 'Bridal Bouquet', ka: 'პატარძლის თაიგული' },
    blurb: { en: 'A white bouquet on warm stone', ka: 'თეთრი თაიგული თბილ ქვაზე' },
    tags: ['bouquet', 'wedding', 'flowers', 'თაიგული', 'ქორწილი'],
    font: 'archivo',
    paper: '#dcd3c9',
    zonePaper: '#c0af9c',
    ink: '#1f1c19',
    accents: ['#1f1c19', '#544e18'],
    subject: { left: 0.88, top: 25.75, w: 91.5, r: 0.984 },
  },
  {
    id: 'line-wedding-cake',
    look: 'line',
    category: 'wedding',
    label: { en: 'Wedding Cake', ka: 'საქორწილო ტორტი' },
    blurb: { en: 'Doves, a tiered cake and champagne', ka: 'მტრედები, მრავალიარუსა ტორტი და შამპანიური' },
    tags: ['wedding', 'cake', 'doves', 'ქორწილი', 'ტორტი'],
    font: 'pinyon-montserrat',
    paper: '#F5F1E9',
    ink: '#5C4A3A',
    accents: ['#5C4A3A', '#a8141b', '#1f3a68', '#2e5a3e', '#6b2d5c', '#1c1c1c'],
    papers: ['#F5F1E9', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.253 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.741 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.627 },
    },
    zone: [15, 70],
  },
  {
    id: 'line-wedding-bells',
    look: 'line',
    category: 'wedding',
    label: { en: 'Wedding Bells', ka: 'საქორწილო ზარები' },
    blurb: { en: 'Bells, a vintage car and an urn of flowers', ka: 'ზარები, რეტრო ავტომობილი და ყვავილების ლარნაკი' },
    tags: ['wedding', 'bells', 'car', 'ქორწილი', 'ზარები'],
    font: 'pinyon-montserrat',
    paper: '#EEF1F5',
    ink: '#3A4A6B',
    accents: ['#3A4A6B', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#EEF1F5', '#f6f1e7', '#f4e6dd', '#e9efe6', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.264 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 0.836 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 33.1, r: 1.815 },
    },
    zone: [15, 70],
  },
  {
    id: 'wc-over-the-moon',
    look: 'watercolor',
    category: 'baby',
    label: { en: 'Over the Moon', ka: 'მთვარე და ვარსკვლავები' },
    blurb: { en: 'A teddy with a star balloon, a pram and a bunny balloon', ka: 'დათუნია ვარსკვლავით, ეტლი და ბაჭიას ბუშტი' },
    tags: ['moon', 'stars', 'baby shower', 'teddy', 'მთვარე', 'ბეიბი შაუერი'],
    font: 'fredoka',
    paper: '#eaf2f6',
    zonePaper: '#deebf1',
    ink: '#44557A',
    accents: ['#577bb0', '#9a7518', '#3f5f8f', '#b0607e', '#44557a'],
    typeset: 'soft',
    date: 'block',
    zone: [22, 64],
    cx: 12,
    pieces: [
      { key: 'balloon', pos: { left: 7, top: 11 }, w: 15, r: 1.699, motion: 'drift' },
      { key: 'teddy', pos: { left: 4, bottom: 4 }, w: 25, r: 1.781, motion: 'hop' },
      { key: 'pram', pos: { right: 3, bottom: 5 }, w: 31, r: 1.066, motion: 'bob' },
    ],
    defaultSwatch: '#bcd5e6',
    colorways: [
      { key: 'blush', label: { en: 'Blush pink', ka: 'ვარდისფერი' }, swatch: '#f1c9cf', files: ['bg', 'thumb', 'pram', 'balloon'], accent: '#b0607e' },
    ],
  },
  {
    id: 'wc-woodland-baby',
    look: 'watercolor',
    category: 'baby',
    label: { en: 'Woodland Baby', ka: 'ტყის პატარა' },
    blurb: { en: 'A sleeping fox, a baby deer and an owl', ka: 'მძინარე მელია, შვლის ნუკრი და ბუ' },
    tags: ['woodland', 'forest', 'fox', 'deer', 'baby shower', 'ტყე', 'მელია', 'ბეიბი შაუერი'],
    font: 'fredoka',
    paper: '#fbf7eb',
    zonePaper: '#faf2e1',
    ink: '#4A5240',
    accents: ['#5f7f4e', '#d7712b', '#9f7a53', '#4a5240'],
    typeset: 'soft',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'fox', pos: { left: 1, bottom: 3 }, w: 32, r: 0.887, motion: 'breathe' },
      { key: 'deer', pos: { right: 3, bottom: 3 }, w: 23.8, r: 1.705, motion: 'bob' },
      { key: 'owl', pos: { right: 4, top: 9 }, w: 21, r: 1.028, motion: 'sway' },
    ],
    defaultSwatch: '#faf3e4',
    colorways: [
      { key: 'blush', label: { en: 'Blush pink', ka: 'ვარდისფერი' }, swatch: '#f1c9cf', files: ['bg', 'thumb'] },
      { key: 'blue', label: { en: 'Powder blue', ka: 'ღია ცისფერი' }, swatch: '#bcd5e6', files: ['bg', 'thumb'] },
    ],
  },
  {
    id: 'wc-wild-one',
    look: 'watercolor',
    category: 'baby',
    label: { en: 'Wild One', ka: 'პატარა ველური' },
    blurb: { en: 'A lion cub in a party hat, a baby elephant and balloons', ka: 'ლეკვი-ლომი ქუდით, სპილოს ბაჭია და ბუშტები' },
    tags: ['wild one', 'first birthday', 'safari', 'lion', 'პირველი დაბადების დღე', 'ლომი'],
    font: 'luckiest-guy',
    paper: '#fcf1de',
    zonePaper: '#fbe9cb',
    ink: '#4A3B22',
    accents: ['#c26e28', '#9d7539', '#4a3b22'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'lion', pos: { left: 1, bottom: 3 }, w: 23.7, r: 1.837, motion: 'hop' },
      { key: 'elephant', pos: { right: 3, bottom: 3 }, w: 25.7, r: 1.571, motion: 'bob' },
      { key: 'balloons', pos: { right: 4, top: 9 }, w: 13, r: 1.728, motion: 'drift' },
    ],
    defaultSwatch: '#fbebd1',
    colorways: [
      { key: 'blush', label: { en: 'Blush pink', ka: 'ვარდისფერი' }, swatch: '#f1c9cf', files: ['bg', 'thumb'] },
      { key: 'blue', label: { en: 'Powder blue', ka: 'ღია ცისფერი' }, swatch: '#bcd5e6', files: ['bg', 'thumb'] },
    ],
  },
  {
    id: 'wc-little-lamb',
    look: 'watercolor',
    category: 'baby',
    label: { en: 'Little Lamb', ka: 'პატარა ბატკანი' },
    blurb: { en: 'A fluffy lamb, a plush bunny and a ribbon kite', ka: 'ფუმფულა ბატკანი, ბაჭია-სათამაშო და ფრანი' },
    tags: ['lamb', 'sheep', 'baby shower', 'christening', 'ბატკანი', 'ბეიბი შაუერი'],
    font: 'fredoka',
    paper: '#fdf8ec',
    zonePaper: '#fcf4e2',
    ink: '#4E4A40',
    accents: ['#8c7a55', '#4e4a40'],
    typeset: 'soft',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'lamb', pos: { left: 1, bottom: 3 }, w: 32, r: 0.883, motion: 'breathe' },
      { key: 'bunny', pos: { right: 3, bottom: 3 }, w: 29, r: 1.153, motion: 'sway' },
      { key: 'kite', pos: { right: 4, top: 9 }, w: 14.4, r: 1.557, motion: 'drift' },
    ],
    defaultSwatch: '#fdf5e5',
    colorways: [
      { key: 'blush', label: { en: 'Blush pink', ka: 'ვარდისფერი' }, swatch: '#f1c9cf', files: ['bg', 'thumb'] },
      { key: 'blue', label: { en: 'Powder blue', ka: 'ღია ცისფერი' }, swatch: '#bcd5e6', files: ['bg', 'thumb'] },
    ],
  },
  {
    id: 'bot-olive',
    look: 'botanical',
    category: 'baby',
    label: { en: 'Olive Branch', ka: 'ზეთისხილის რტო' },
    blurb: { en: 'A slender olive branch for a christening', ka: 'ზეთისხილის ნაზი რტო ნათლობისთვის' },
    tags: ['olive', 'christening', 'baptism', 'ზეთისხილი', 'ნათლობა'],
    font: 'delafield-cormorant',
    paper: '#f1f1ea',
    ink: '#353a2f',
    accents: ['#353a2f', '#655a1a', '#5d6b4f', '#4b3a55', '#7a6a3a'],
    papers: ['#f1f1ea', '#f3eee4', '#e6eadf', '#e5ebee', '#2a3326', '#f5ece6'],
    plant: { left: 4, bottom: -3, width: 27, r: 3.67 },
    copy: { left: 38, right: 6, top: 24 },
  },
  {
    id: 'bot-babys-breath',
    look: 'botanical',
    category: 'baby',
    label: { en: 'Baby\'s Breath', ka: 'გიფსოფილა' },
    blurb: { en: 'Airy baby\'s breath and eucalyptus', ka: 'ჰაეროვანი გიფსოფილა და ევკალიპტი' },
    tags: ['baby\'s breath', 'christening', 'baby', 'ნათლობა', 'გიფსოფილა'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 2.109 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'line-little-one',
    look: 'line',
    category: 'baby',
    label: { en: 'Little One', ka: 'პატარა' },
    blurb: { en: 'Moons and stars, a pram and a rocking horse', ka: 'მთვარე და ვარსკვლავები, ეტლი და სარწეველა ცხენი' },
    tags: ['baby shower', 'pram', 'ბეიბი შაუერი', 'ეტლი'],
    font: 'pinyon-montserrat',
    paper: '#eef3f7',
    ink: '#3d5a80',
    accents: ['#3d5a80', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#eef3f7', '#f6f1e7', '#f4e6dd', '#e9efe6', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.24 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.405 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.333 },
    },
    zone: [14, 70],
  },
  {
    id: 'line-special-delivery',
    look: 'line',
    category: 'baby',
    label: { en: 'Special Delivery', ka: 'განსაკუთრებული ამანათი' },
    blurb: { en: 'A stork with a bundle and a cradle', ka: 'ყარყატი ამანათით და აკვანი' },
    tags: ['stork', 'baby shower', 'cradle', 'ყარყატი', 'ბეიბი შაუერი'],
    font: 'pinyon-montserrat',
    paper: '#EEF3F7',
    ink: '#4A6A8A',
    accents: ['#4A6A8A', '#a8141b', '#1f3a68', '#2e5a3e', '#6b2d5c', '#7a4a26'],
    papers: ['#EEF3F7', '#f6f1e7', '#f4e6dd', '#e9efe6', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.195 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.211 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.754 },
    },
    zone: [14, 70],
  },
  {
    id: 'wc-midnight-gala',
    look: 'watercolor',
    category: 'corporate',
    label: { en: 'Midnight Gala', ka: 'შუაღამის გალა' },
    blurb: { en: 'Gold streamers over a night skyline', ka: 'ოქროს სერპანტინი ღამის ქალაქზე' },
    tags: ['gala', 'night', 'gold', 'corporate', 'გალა', 'ღამე', 'კორპორატიული'],
    font: 'cinzel',
    paper: '#011336',
    zonePaper: '#011336',
    dark: true,
    ink: '#EDE5D2',
    accents: ['#e6c780', '#d9a441', '#f2e8d0', '#d8a48f', '#dbdee3'],
    typeset: 'deco',
    date: 'line',
    zone: [19, 64],
    cx: 11,
    pieces: [
      { key: 'popper', pos: { left: 3, bottom: 10 }, w: 25, r: 1.394, motion: 'hop' },
      { key: 'flutes', pos: { right: 4, bottom: 10 }, w: 23, r: 1.444, motion: 'sway' },
    ],
  },
  {
    id: 'photo-morning-meetup',
    look: 'photo',
    category: 'corporate',
    label: { en: 'Morning Meetup', ka: 'დილის შეხვედრა' },
    blurb: { en: 'Cappuccino and a linen notebook', ka: 'კაპუჩინო და სელის ბლოკნოტი' },
    tags: ['meetup', 'coffee', 'breakfast', 'შეხვედრა', 'ყავა'],
    font: 'archivo',
    paper: '#d8d4cf',
    zonePaper: '#b9b2a8',
    ink: '#1f2122',
    accents: ['#1f2122', '#5e3c16', '#23364a', '#3f2f25'],
    subject: { left: 4.25, top: 31.92, w: 89, r: 0.74 },
  },
  {
    id: 'photo-launch-day',
    look: 'photo',
    category: 'corporate',
    label: { en: 'Launch Day', ka: 'პრეზენტაციის დღე' },
    blurb: { en: 'A white gift box on a plinth', ka: 'თეთრი ყუთი პედესტალზე' },
    tags: ['launch', 'product', 'corporate', 'პრეზენტაცია'],
    font: 'archivo',
    paper: '#d7d4d0',
    zonePaper: '#b5b1aa',
    ink: '#1d1c1b',
    accents: ['#1d1c1b'],
    subject: { left: 20.5, top: 32.5, w: 54.37, r: 1.122 },
  },
  {
    id: 'photo-summit',
    look: 'photo',
    category: 'corporate',
    label: { en: 'The Summit', ka: 'სამიტი' },
    blurb: { en: 'A studio microphone and a glass of water', ka: 'მიკროფონი და ჭიქა წყალი' },
    tags: ['summit', 'conference', 'talk', 'სამიტი', 'კონფერენცია'],
    font: 'archivo',
    paper: '#d3d0cd',
    zonePaper: '#afaaa4',
    ink: '#1c1b1a',
    accents: ['#1c1b1a'],
    subject: { left: 30.75, top: 29.75, w: 47.62, r: 1.142 },
  },
  {
    id: 'photo-awards-night',
    look: 'photo',
    category: 'corporate',
    label: { en: 'Awards Night', ka: 'დაჯილდოების ღამე' },
    blurb: { en: 'A golden trophy in low light', ka: 'ოქროს ჯილდო მკრთალ შუქზე' },
    tags: ['awards', 'trophy', 'gala', 'დაჯილდოება', 'გალა'],
    font: 'archivo',
    paper: '#1a1916',
    zonePaper: '#1a1916',
    dark: true,
    ink: '#f1e9da',
    accents: ['#f1e9da', '#b38436'],
    subject: { left: 33.12, top: 33.58, w: 33.75, r: 1.704 },
  },
  {
    id: 'photo-office-party',
    look: 'photo',
    category: 'corporate',
    label: { en: 'Office Party', ka: 'ოფისის წვეულება' },
    blurb: { en: 'Confetti and champagne on a white desk', ka: 'კონფეტი და შამპანიური თეთრ მაგიდაზე' },
    tags: ['office', 'party', 'work', 'ოფისი', 'წვეულება'],
    font: 'archivo',
    paper: '#edebe8',
    zonePaper: '#dedbd6',
    ink: '#242322',
    accents: ['#242322', '#90763c'],
    subject: { left: 15, top: 24.33, w: 85, r: 0.76 },
  },
  {
    id: 'photo-retirement-toast',
    look: 'photo',
    category: 'corporate',
    label: { en: 'Retirement Toast', ka: 'საპენსიო სადღეგრძელო' },
    blurb: { en: 'A whisky glass, a leather notebook and glasses', ka: 'ვისკის ჭიქა, ტყავის ბლოკნოტი და სათვალე' },
    tags: ['retirement', 'farewell', 'toast', 'პენსია', 'გამოსვლა'],
    font: 'archivo',
    paper: '#cdae93',
    zonePaper: '#a46c3a',
    ink: '#1a1109',
    accents: ['#1a1109', '#e5d8cd'],
    subject: { left: 5.25, top: 30.42, w: 90.62, r: 0.677 },
  },
  {
    id: 'photo-team-offsite',
    look: 'photo',
    category: 'corporate',
    label: { en: 'Team Offsite', ka: 'გუნდური გასვლა' },
    blurb: { en: 'A map, a compass and an enamel mug', ka: 'რუკა, კომპასი და მინანქრის ჭიქა' },
    tags: ['team', 'offsite', 'retreat', 'გუნდი', 'გასვლა'],
    font: 'archivo',
    paper: '#dacfc3',
    zonePaper: '#bca891',
    ink: '#1e1b17',
    accents: ['#1e1b17', '#503819'],
    subject: { left: 3.5, top: 34.25, w: 84.88, r: 0.851 },
  },
  {
    id: 'line-launch-party',
    look: 'line',
    category: 'corporate',
    label: { en: 'Launch Party', ka: 'სტარტაპის წვეულება' },
    blurb: { en: 'A rocket, a laptop and a desk lamp', ka: 'რაკეტა, ლეპტოპი და მაგიდის ლამპა' },
    tags: ['startup', 'launch', 'tech', 'სტარტაპი', 'ტექნოლოგია'],
    font: 'pinyon-montserrat',
    paper: '#EEF0F2',
    ink: '#23364A',
    accents: ['#23364A', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26'],
    papers: ['#EEF0F2', '#f6f1e7', '#f4e6dd', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.237 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.692 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.194 },
    },
    zone: [14, 70],
  },
  {
    id: 'line-gala-dinner',
    look: 'line',
    category: 'corporate',
    label: { en: 'Gala Dinner', ka: 'საზეიმო ვახშამი' },
    blurb: { en: 'A candlelit table and champagne on ice', ka: 'სანთლებით მორთული მაგიდა და შამპანიური' },
    tags: ['gala', 'dinner', 'formal', 'გალა', 'ვახშამი'],
    font: 'pinyon-montserrat',
    paper: '#F2EFE8',
    ink: '#2B2B2B',
    accents: ['#2B2B2B', '#a8141b', '#1f3a68', '#2e5a3e', '#6b2d5c', '#7a4a26'],
    papers: ['#F2EFE8', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.302 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.579 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 32.7, r: 1.837 },
    },
    zone: [17, 70],
  },
  {
    id: 'wc-winter-pine',
    look: 'watercolor',
    category: 'holiday',
    label: { en: 'Snowy New Year', ka: 'თოვლიანი ახალი წელი' },
    blurb: { en: 'Pine garland, a snowman, cocoa and presents', ka: 'ნაძვის გირლანდა, თოვლის ბაბუ, კაკაო და საჩუქრები' },
    tags: ['new year', 'christmas', 'winter', 'snow', 'ახალი წელი', 'შობა', 'ზამთარი'],
    font: 'lobster',
    paper: '#f8fafd',
    zonePaper: '#f4f8fc',
    ink: '#2B4052',
    accents: ['#b02f2b', '#2e5e47', '#9a6b2f', '#1f4f7a', '#313232'],
    typeset: 'festive',
    date: 'block',
    zone: [25, 69],
    cx: 12,
    pieces: [
      { key: 'snowman', pos: { left: 2, bottom: 3 }, w: 26, r: 1.469, motion: 'hop' },
      { key: 'cocoa', pos: { left: 30, bottom: 2 }, w: 14, r: 1.557, motion: 'breathe' },
      { key: 'presents', pos: { left: 51, bottom: 2 }, w: 21, r: 1.499, motion: 'bob' },
    ],
  },
  {
    id: 'wc-rtveli-supra',
    look: 'watercolor',
    category: 'holiday',
    label: { en: 'Rtveli Supra', ka: 'რთვლის სუფრა' },
    blurb: { en: 'Vines, a clay wine jug, churchkhela and khachapuri', ka: 'ვაზი, დოქი, ჩურჩხელა და ხაჭაპური' },
    tags: ['supra', 'rtveli', 'wine', 'feast', 'georgian', 'სუფრა', 'რთველი', 'ღვინო'],
    font: 'abril',
    paper: '#fdf3df',
    zonePaper: '#fcecce',
    ink: '#3E2A1D',
    accents: ['#7a2233', '#b84f16', '#5c610d', '#9a6a12', '#4b2a5e', '#322f29'],
    typeset: 'rustic',
    date: 'line',
    zone: [25, 68],
    cx: 18,
    pieces: [
      { key: 'churchkhela', pos: { left: 4, top: 11 }, w: 9, r: 3.21, motion: 'sway' },
      { key: 'doqi', pos: { left: 1, bottom: 4 }, w: 34, r: 1.061, motion: 'breathe' },
      { key: 'khachapuri', pos: { right: 3, bottom: 5 }, w: 33, r: 0.65, motion: 'bob' },
    ],
    kicker: { en: 'Gaumarjos! You’re invited to', ka: 'გაუმარჯოს! გეპატიჟებით' },
  },
  {
    id: 'wc-easter-bloom',
    look: 'watercolor',
    category: 'holiday',
    label: { en: 'Easter Bloom', ka: 'აღდგომის ყვავილები' },
    blurb: { en: 'A basket of red eggs, a bunny and blossoms', ka: 'წითელ კვერცხებით სავსე კალათა, ბაჭია და ყვავილები' },
    tags: ['easter', 'spring', 'eggs', 'bunny', 'აღდგომა', 'კვერცხი', 'გაზაფხული'],
    font: 'pinyon-bricolage',
    paper: '#fef9e6',
    zonePaper: '#fdf7d9',
    ink: '#3F4A33',
    accents: ['#b23a3a', '#a35a24', '#889611', '#c2191b', '#988366', '#3f4a33'],
    typeset: 'garden-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'basket', pos: { left: 1, bottom: 3 }, w: 32, r: 0.967, motion: 'breathe' },
      { key: 'bunny', pos: { right: 3, bottom: 3 }, w: 21.4, r: 1.891, motion: 'hop' },
      { key: 'bird', pos: { right: 4, top: 9 }, w: 21, r: 0.89, motion: 'sway' },
    ],
  },
  {
    id: 'wc-friendly-spooks',
    look: 'watercolor',
    category: 'holiday',
    label: { en: 'Friendly Spooks', ka: 'ჰელოუინის სტუმრები' },
    blurb: { en: 'A grinning pumpkin, a friendly ghost and a witch hat', ka: 'მომღიმარი გოგრა, კეთილი მოჩვენება და ჯადოქრის ქუდი' },
    tags: ['halloween', 'pumpkin', 'ghost', 'autumn', 'ჰელოუინი', 'გოგრა'],
    font: 'luckiest-guy',
    paper: '#fef0e2',
    zonePaper: '#fde9d3',
    ink: '#3A2A4A',
    accents: ['#da611c', '#b57123', '#a07b71', '#613f79', '#3a2a4a'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'pumpkin', pos: { left: 1, bottom: 3 }, w: 32, r: 1.156, motion: 'hop' },
      { key: 'ghost', pos: { right: 4, top: 9 }, w: 17.6, r: 1.278, motion: 'drift' },
      { key: 'hat', pos: { right: 3, bottom: 3 }, w: 29, r: 0.956, motion: 'sway' },
    ],
  },
  {
    id: 'wc-tbilisoba',
    look: 'watercolor',
    category: 'holiday',
    label: { en: 'Tbilisoba', ka: 'თბილისობა' },
    blurb: { en: 'Old Tbilisi balconies, grapes and pomegranates', ka: 'ძველი თბილისის აივნები, ყურძენი და ბროწეული' },
    tags: ['tbilisoba', 'tbilisi', 'autumn', 'festival', 'თბილისობა', 'თბილისი', 'შემოდგომა'],
    font: 'abril',
    paper: '#fef4d9',
    zonePaper: '#fdedc4',
    ink: '#3E2A1D',
    accents: ['#8e2b2b', '#c95e1c', '#a97315', '#b00f15', '#3e2a1d'],
    typeset: 'rustic',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'basket', pos: { left: 1, bottom: 3 }, w: 32, r: 0.898, motion: 'breathe' },
      { key: 'jug', pos: { right: 3, bottom: 3 }, w: 29, r: 1.247, motion: 'bob' },
      { key: 'leaves', pos: { right: 4, top: 9 }, w: 18.9, r: 1.19, motion: 'sway' },
    ],
  },
  {
    id: 'wc-sweethearts',
    look: 'watercolor',
    category: 'holiday',
    label: { en: 'Sweethearts', ka: 'შეყვარებულთა დღე' },
    blurb: { en: 'Lovebirds, red roses and a box of chocolates', ka: 'ბეღურები, წითელი ვარდები და შოკოლადის ყუთი' },
    tags: ['valentine', 'love', 'hearts', 'roses', 'სიყვარული', 'ვალენტინობა', 'გული'],
    font: 'great-vibes',
    paper: '#feefee',
    zonePaper: '#fde6e5',
    ink: '#5A2E36',
    accents: ['#b32a40', '#db3935', '#817338', '#9b6244', '#5a2e36'],
    typeset: 'glam-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'birds', pos: { right: 4, top: 9 }, w: 21, r: 0.712, motion: 'sway' },
      { key: 'chocolates', pos: { left: 1, bottom: 3 }, w: 32, r: 0.862, motion: 'breathe' },
      { key: 'roses', pos: { right: 3, bottom: 3 }, w: 26.8, r: 1.512, motion: 'bob' },
    ],
  },
  {
    id: 'bot-tulips',
    look: 'botanical',
    category: 'holiday',
    label: { en: 'Spring Tulips', ka: 'გაზაფხულის ტიტები' },
    blurb: { en: 'Red and pink tulips for the 8th of March', ka: 'წითელი და ვარდისფერი ტიტები 8 მარტისთვის' },
    tags: ['tulips', 'women\'s day', 'march 8', 'spring', 'ტიტები', '8 მარტი', 'გაზაფხული'],
    font: 'delafield-cormorant',
    paper: '#f5efe6',
    ink: '#3a2f2c',
    accents: ['#3a2f2c', '#c9412f', '#b85a63', '#3f6b5b', '#8e6452'],
    papers: ['#f5efe6', '#fbece8', '#edf2ea', '#f6efd9', '#26302b', '#2e2426'],
    plant: { left: -22, bottom: -2, width: 58, r: 1.626 },
    copy: { left: 42, right: 4, top: 19 },
  },
  {
    id: 'bot-lilac',
    look: 'botanical',
    category: 'holiday',
    label: { en: 'Lilac', ka: 'იასამანი' },
    blurb: { en: 'Lilac branches for Mother\'s Day', ka: 'იასამნის ტოტები დედის დღისთვის' },
    tags: ['lilac', 'mother\'s day', 'spring', 'იასამანი', 'დედის დღე'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#607335', '#763e62', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 1.922 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'photo-harvest-supper',
    look: 'photo',
    category: 'holiday',
    label: { en: 'Harvest Supper', ka: 'მოსავლის ვახშამი' },
    blurb: { en: 'Bread, pears and figs on linen', ka: 'პური, მსხალი და ლეღვი სელზე' },
    tags: ['harvest', 'autumn', 'dinner', 'thanksgiving', 'მოსავალი', 'შემოდგომა'],
    font: 'archivo',
    paper: '#ddd2c4',
    zonePaper: '#c1ac94',
    ink: '#1f1c18',
    accents: ['#1f1c18', '#815417', '#762a18'],
    subject: { left: 5, top: 26, w: 92.25, r: 0.734 },
  },
  {
    id: 'line-christmas-dinner',
    look: 'line',
    category: 'holiday',
    label: { en: 'Christmas Dinner', ka: 'საშობაო ვახშამი' },
    blurb: { en: 'Pine garland, a candelabra and a little tree', ka: 'ნაძვის გირლანდა, შანდალი და პატარა ნაძვი' },
    tags: ['christmas', 'dinner', 'შობა', 'ვახშამი'],
    font: 'pinyon-montserrat',
    paper: '#f3ece2',
    ink: '#8f1d22',
    accents: ['#8f1d22', '#1f3a68', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#f3ece2', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.233 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 27.5, r: 2.184 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.601 },
    },
    zone: [14, 70],
  },
  {
    id: 'line-midnight-countdown',
    look: 'line',
    category: 'holiday',
    label: { en: 'Midnight Countdown', ka: 'ახალი წლის ღამე' },
    blurb: { en: 'An ornate clock and champagne on ice', ka: 'ძველებური ზარ-საათი და შამპანიური' },
    tags: ['new year', 'midnight', 'champagne', 'ახალი წელი', 'შამპანიური'],
    font: 'pinyon-montserrat',
    paper: '#EEF0F5',
    ink: '#1F3A68',
    accents: ['#1F3A68', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#EEF0F5', '#f6f1e7', '#f4e6dd', '#e9efe6', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.268 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.601 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.705 },
    },
    zone: [16, 70],
  },
  {
    id: 'line-haunted-night',
    look: 'line',
    category: 'holiday',
    label: { en: 'Haunted Night', ka: 'ჰელოუინის ღამე' },
    blurb: { en: 'Bats, a haunted house and a cauldron', ka: 'ღამურები, მოჩვენებათა სახლი და ქვაბი' },
    tags: ['halloween', 'haunted', 'bats', 'ჰელოუინი'],
    font: 'pinyon-montserrat',
    paper: '#F3ECE2',
    ink: '#4A2A5A',
    accents: ['#4A2A5A', '#a8141b', '#2e5a3e', '#7a4a26', '#1c1c1c'],
    papers: ['#F3ECE2', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.253 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.601 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.493 },
    },
    zone: [15, 70],
  },
  {
    id: 'wc-pool-party',
    look: 'watercolor',
    category: 'party',
    label: { en: 'Pool Party', ka: 'აუზის წვეულება' },
    blurb: { en: 'Palm leaves, a flamingo float and a tropical cocktail', ka: 'პალმის ფოთლები, ფლამინგო და ტროპიკული კოქტეილი' },
    tags: ['pool', 'summer', 'tropical', 'flamingo', 'აუზი', 'ზაფხული'],
    font: 'bebas',
    paper: '#fef7de',
    zonePaper: '#fdf3cd',
    ink: '#24414F',
    accents: ['#ec5642', '#d93f78', '#23838e', '#9e6c12', '#3f7f2e', '#333129'],
    typeset: 'poster-bold',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'flamingo', pos: { left: 1, bottom: 8 }, w: 35, r: 0.923, motion: 'bob' },
      { key: 'cocktail', pos: { right: 4, bottom: 6 }, w: 23, r: 1.635, motion: 'sway' },
    ],
  },
  {
    id: 'wc-karaoke-night',
    look: 'watercolor',
    category: 'party',
    label: { en: 'Karaoke Night', ka: 'კარაოკე ღამე' },
    blurb: { en: 'A retro microphone, a cassette and speakers', ka: 'რეტრო მიკროფონი, კასეტა და დინამიკები' },
    tags: ['karaoke', 'music', 'singing', 'კარაოკე', 'მუსიკა', 'სიმღერა'],
    font: 'bebas',
    paper: '#fee0e7',
    zonePaper: '#fdcfdb',
    ink: '#2E2A55',
    accents: ['#d5397a', '#291e79', '#ae5475', '#927037', '#7f42a8', '#2e2a55'],
    typeset: 'poster-bold',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'mic', pos: { left: 1, bottom: 3 }, w: 13, r: 3.355, motion: 'sway' },
      { key: 'cassette', pos: { right: 4, top: 9 }, w: 21, r: 0.95, motion: 'drift' },
      { key: 'speaker', pos: { right: 3, bottom: 3 }, w: 28.6, r: 1.417, motion: 'bob' },
    ],
  },
  {
    id: 'wc-game-night',
    look: 'watercolor',
    category: 'party',
    label: { en: 'Game Night', ka: 'სამაგიდო თამაშების ღამე' },
    blurb: { en: 'Dice, a chess knight and a pizza box', ka: 'კამათლები, ჭადრაკის ცხენი და პიცის ყუთი' },
    tags: ['games', 'board games', 'cards', 'dice', 'თამაშები', 'კამათელი'],
    font: 'luckiest-guy',
    paper: '#fef7eb',
    zonePaper: '#fdf3e0',
    ink: '#2A2A2A',
    accents: ['#2f6fb0', '#cd0904', '#9e7a47', '#de6c1e', '#2a2a2a'],
    typeset: 'cartoon',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'dice', pos: { right: 4, top: 9 }, w: 18.1, r: 1.241, motion: 'spin' },
      { key: 'pizza', pos: { left: 1, bottom: 3 }, w: 32, r: 1.153, motion: 'bob' },
      { key: 'chess', pos: { right: 3, bottom: 3 }, w: 29, r: 1.206, motion: 'breathe' },
    ],
  },
  {
    id: 'photo-wine-night',
    look: 'photo',
    category: 'party',
    label: { en: 'Wine Night', ka: 'ღვინის საღამო' },
    blurb: { en: 'A sunlit glass of red wine, poster style', ka: 'მზის შუქზე ღვინის ჭიქა, პოსტერის სტილში' },
    tags: ['wine', 'tasting', 'ღვინო', 'დეგუსტაცია'],
    font: 'archivo',
    paper: '#ede7dd',
    zonePaper: '#dfd3c2',
    ink: '#23201c',
    accents: ['#23201c', '#84060f', '#4b1f3f', '#5b5309', '#6b4a2a'],
    subject: { left: 7.12, top: 29.25, w: 80.62, r: 0.845 },
  },
  {
    id: 'photo-disco-night',
    look: 'photo',
    category: 'party',
    label: { en: 'Disco Night', ka: 'დისკო ღამე' },
    blurb: { en: 'A disco ball scattering sunlight', ka: 'დისკო-ბურთი მზის ანარეკლებით' },
    tags: ['disco', 'dance', 'party', 'დისკო', 'ცეკვა'],
    font: 'archivo',
    paper: '#ddd3c6',
    zonePaper: '#c2af97',
    ink: '#24201b',
    accents: ['#24201b', '#4f3d1f', '#23324a', '#4a2238'],
    subject: { left: 30.88, top: 35.42, w: 37, r: 1.03 },
  },
  {
    id: 'photo-brunch-club',
    look: 'photo',
    category: 'party',
    label: { en: 'Brunch Club', ka: 'ბრანჩ-კლუბი' },
    blurb: { en: 'Pancakes, berries and orange juice', ka: 'ბლინები, კენკრა და ფორთოხლის წვენი' },
    tags: ['brunch', 'breakfast', 'ბრანჩი', 'საუზმე'],
    font: 'archivo',
    paper: '#f8eec7',
    zonePaper: '#f3df9a',
    ink: '#272419',
    accents: ['#272419', '#7c714a', '#a46e08', '#944406'],
    subject: { left: 8.62, top: 26.67, w: 80.62, r: 0.831 },
  },
  {
    id: 'photo-mtsvadi',
    look: 'photo',
    category: 'party',
    label: { en: 'Mtsvadi Cookout', ka: 'მწვადის პიკნიკი' },
    blurb: { en: 'Grilled skewers with onion and pomegranate', ka: 'მწვადი ხახვით და ბროწეულით' },
    tags: ['bbq', 'grill', 'mtsvadi', 'cookout', 'მწვადი', 'მაყალი'],
    font: 'archivo',
    paper: '#cdbbaa',
    zonePaper: '#a48465',
    ink: '#1a1510',
    accents: ['#1a1510', '#f4eeea', '#810d1b'],
    subject: { left: 0, top: 29.5, w: 98.75, r: 0.718 },
  },
  {
    id: 'photo-movie-night',
    look: 'photo',
    category: 'party',
    label: { en: 'Movie Night', ka: 'კინოს ღამე' },
    blurb: { en: 'A striped popcorn bucket', ka: 'ზოლიანი ყუთი ბატიბუტით' },
    tags: ['movie', 'cinema', 'popcorn', 'კინო', 'ბატიბუტი'],
    font: 'archivo',
    paper: '#efe9e1',
    zonePaper: '#e1d7c8',
    ink: '#242220',
    accents: ['#242220', '#a4261b', '#956b2a'],
    subject: { left: 26.5, top: 31.5, w: 46.12, r: 1.339 },
  },
  {
    id: 'photo-masquerade',
    look: 'photo',
    category: 'party',
    label: { en: 'Masquerade', ka: 'მასკარადი' },
    blurb: { en: 'A gold lace mask on dark velvet', ka: 'ოქროს მაქმანის ნიღაბი მუქ ხავერდზე' },
    tags: ['masquerade', 'mask', 'ball', 'მასკარადი', 'ნიღაბი'],
    font: 'archivo',
    paper: '#120403',
    zonePaper: '#120403',
    dark: true,
    ink: '#f1e9da',
    accents: ['#f1e9da', '#7d5755', '#bb8747'],
    subject: { left: 0, top: 25.67, w: 100, r: 0.616 },
  },
  {
    id: 'line-tea-party',
    look: 'line',
    category: 'party',
    label: { en: 'Garden Tea Party', ka: 'ჩაის წვეულება' },
    blurb: { en: 'Ribbon bows, a teacup tower and a cake stand', ka: 'ბაფთები, ფინჯნების კოშკი და ტორტის სადგამი' },
    tags: ['tea', 'garden', 'high tea', 'ჩაი', 'ბაღი'],
    font: 'pinyon-montserrat',
    paper: '#f4e6dd',
    ink: '#a8141b',
    accents: ['#a8141b', '#1f3a68', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#f4e6dd', '#e9efe6', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.372 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 24.8, r: 2.419 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 29.8, r: 2.013 },
    },
    zone: [20, 70],
  },
  {
    id: 'line-graduation',
    look: 'line',
    category: 'party',
    label: { en: 'Graduation', ka: 'გამოსაშვები' },
    blurb: { en: 'Laurels, books and a wise owl', ka: 'დაფნის გვირგვინი, წიგნები და ბუ' },
    tags: ['graduation', 'school', 'university', 'გამოსაშვები', 'სკოლა'],
    font: 'pinyon-montserrat',
    paper: '#f2efe8',
    ink: '#1f3a68',
    accents: ['#1f3a68', '#a8141b', '#2e5a3e', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#f2efe8', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.323 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.172 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.198 },
    },
    zone: [18, 70],
  },
  {
    id: 'line-housewarming',
    look: 'line',
    category: 'party',
    label: { en: 'Housewarming', ka: 'ახალი ბინის ზეიმი' },
    blurb: { en: 'A cosy house, a monstera and keys', ka: 'მყუდრო სახლი, მონსტერა და გასაღებები' },
    tags: ['housewarming', 'new home', 'ახალი ბინა', 'სახლი'],
    font: 'pinyon-montserrat',
    paper: '#F1EEE6',
    ink: '#3E5A4A',
    accents: ['#3E5A4A', '#a8141b', '#1f3a68', '#6b2d5c', '#7a4a26', '#1c1c1c'],
    papers: ['#F1EEE6', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.347 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.478 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 34, r: 1.528 },
    },
    zone: [19, 70],
  },
  {
    id: 'wc-kazbegi-trip',
    look: 'watercolor',
    category: 'outdoor',
    label: { en: 'Kazbegi Trip', ka: 'ყაზბეგის ლაშქრობა' },
    blurb: { en: 'Misty mountains, a hilltop church and a backpack', ka: 'ნისლიანი მთები, გორაკზე ეკლესია და ზურგჩანთა' },
    tags: ['mountains', 'hiking', 'kazbegi', 'trip', 'მთა', 'ლაშქრობა', 'ყაზბეგი'],
    font: 'fredoka',
    paper: '#faf8f0',
    zonePaper: '#f7f4e8',
    ink: '#2F3B45',
    accents: ['#2e5e7a', '#ed5a51', '#9e1b1d', '#ab884b', '#2f3b45'],
    typeset: 'rounded',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'backpack', pos: { left: 1, bottom: 3 }, w: 32, r: 1.33, motion: 'bob' },
      { key: 'mug', pos: { right: 3, bottom: 3 }, w: 29, r: 1.121, motion: 'breathe' },
      { key: 'eagle', pos: { right: 4, top: 9 }, w: 21, r: 0.904, motion: 'drift' },
    ],
  },
  {
    id: 'wc-camp-stars',
    look: 'watercolor',
    category: 'outdoor',
    label: { en: 'Camp Under the Stars', ka: 'ვარსკვლავების ქვეშ' },
    blurb: { en: 'A glowing tent, a campfire and a little owl', ka: 'ანთებული კარავი, კოცონი და ბუ' },
    tags: ['camping', 'tent', 'campfire', 'stars', 'კემპინგი', 'კოცონი', 'ვარსკვლავები'],
    font: 'fredoka',
    paper: '#f3e9f1',
    zonePaper: '#edddea',
    ink: '#2E2E3E',
    accents: ['#2f4a7a', '#a27637', '#7f78ad', '#966e64', '#2e2e3e'],
    typeset: 'rounded',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'tent', pos: { left: 1, bottom: 3 }, w: 32, r: 0.896, motion: 'breathe' },
      { key: 'fire', pos: { right: 3, bottom: 3 }, w: 29, r: 0.885, motion: 'breathe' },
      { key: 'owl', pos: { right: 4, top: 9 }, w: 21, r: 0.908, motion: 'sway' },
    ],
  },
  {
    id: 'wc-beach-day',
    look: 'watercolor',
    category: 'outdoor',
    label: { en: 'Beach Day', ka: 'სანაპიროს დღე' },
    blurb: { en: 'A striped umbrella, a sandcastle and a beach ball', ka: 'ზოლიანი ჩრდილსარი, ქვიშის ციხე და ბურთი' },
    tags: ['beach', 'sea', 'summer', 'ზღვა', 'ზაფხული', 'სანაპირო'],
    font: 'bebas',
    paper: '#fefae2',
    zonePaper: '#fdf7d2',
    ink: '#2A4A5A',
    accents: ['#de5a3a', '#ac8548', '#418f96', '#028cd0', '#c67b20', '#2a4a5a'],
    typeset: 'poster-bold',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'umbrella', pos: { right: 3, bottom: 3 }, w: 29, r: 1.182, motion: 'sway' },
      { key: 'castle', pos: { left: 1, bottom: 3 }, w: 32, r: 1.036, motion: 'breathe' },
      { key: 'ball', pos: { right: 4, top: 9 }, w: 20.9, r: 1.074, motion: 'hop' },
    ],
  },
  {
    id: 'wc-picnic-day',
    look: 'watercolor',
    category: 'outdoor',
    label: { en: 'Picnic Day', ka: 'პიკნიკის დღე' },
    blurb: { en: 'A wicker basket, lemonade and a kite', ka: 'წნული კალათა, ლიმონათი და ფრანი' },
    tags: ['picnic', 'park', 'spring', 'summer', 'პიკნიკი', 'პარკი'],
    font: 'pacifico',
    paper: '#fbf8eb',
    zonePaper: '#f9f4e0',
    ink: '#3E5A2E',
    accents: ['#cc4b3a', '#c47237', '#697527', '#c5786e', '#a28618', '#3e5a2e'],
    typeset: 'bubbly-script',
    date: 'block',
    zone: [22, 66],
    cx: 12,
    pieces: [
      { key: 'basket', pos: { left: 1, bottom: 3 }, w: 32, r: 1.086, motion: 'breathe' },
      { key: 'lemonade', pos: { right: 3, bottom: 3 }, w: 29, r: 1.092, motion: 'bob' },
      { key: 'kite', pos: { right: 4, top: 9 }, w: 15.9, r: 1.409, motion: 'drift' },
    ],
  },
  {
    id: 'photo-trail-morning',
    look: 'photo',
    category: 'outdoor',
    label: { en: 'Trail Morning', ka: 'ლაშქრობის დილა' },
    blurb: { en: 'Hiking boots, a thermos and a map', ka: 'ლაშქრობის ფეხსაცმელი, თერმოსი და რუკა' },
    tags: ['hiking', 'trail', 'mountains', 'ლაშქრობა', 'მთა'],
    font: 'archivo',
    paper: '#dbd1c7',
    zonePaper: '#beac9a',
    ink: '#1e1c19',
    accents: ['#1e1c19', '#482a0f'],
    subject: { left: 17.5, top: 27.75, w: 76.5, r: 0.946 },
  },
  {
    id: 'bot-white-orchid',
    look: 'botanical',
    category: 'elegant',
    label: { en: 'White Orchid', ka: 'თეთრი ორქიდეა' },
    blurb: { en: 'An arching spray of white orchids', ka: 'თეთრი ორქიდეების მოხრილი ტოტი' },
    tags: ['orchid', 'elegant', 'minimal', 'ორქიდეა', 'ელეგანტური'],
    font: 'delafield-cormorant',
    paper: '#f3f0e8',
    ink: '#34332e',
    accents: ['#34332e', '#3b501a', '#8b6043', '#5f6350'],
    papers: ['#f3f0e8', '#f2e7e3', '#e7ebe1', '#ebe5d9', '#223127', '#1f2331'],
    plant: { left: -10, bottom: -2, width: 48, r: 2.117 },
    copy: { left: 41, right: 5, top: 20 },
  },
  {
    id: 'photo-candlelight',
    look: 'photo',
    category: 'elegant',
    label: { en: 'Candlelight', ka: 'სანთლის შუქი' },
    blurb: { en: 'Taper candles and a wine glass in the dark', ka: 'სანთლები და ღვინის ჭიქა სიბნელეში' },
    tags: ['candles', 'dinner', 'elegant', 'სანთლები', 'ვახშამი'],
    font: 'archivo',
    paper: '#12110e',
    zonePaper: '#12110e',
    dark: true,
    ink: '#f1e9da',
    accents: ['#f1e9da', '#c09155', '#8a5832'],
    subject: { left: 28.12, top: 24.5, w: 47, r: 1.638 },
  },
  {
    id: 'line-pearls-perfume',
    look: 'line',
    category: 'elegant',
    label: { en: 'Pearls & Perfume', ka: 'მარგალიტი და სუნამო' },
    blurb: { en: 'Pearl strands, perfume and hat boxes', ka: 'მარგალიტის ძაფები, სუნამო და ქუდის ყუთები' },
    tags: ['pearls', 'perfume', 'elegant', 'მარგალიტი', 'ელეგანტური'],
    font: 'pinyon-montserrat',
    paper: '#F4EDEC',
    ink: '#6A4A5A',
    accents: ['#6A4A5A', '#a8141b', '#1f3a68', '#2e5a3e', '#7a4a26', '#1c1c1c'],
    papers: ['#F4EDEC', '#e9efe6', '#e6eef5', '#fbf1d9', '#2a2a2e'],
    art: {
      garland: { pos: { left: 3, top: 1.5 }, w: 94, r: 0.152 },
      left: { pos: { left: 1.5, bottom: 1.5 }, w: 34, r: 1.475 },
      right: { pos: { right: 1.5, bottom: 1.5 }, w: 31.5, r: 1.903 },
    },
    zone: [14, 70],
  },
]
